import React from 'react';
import { OpeningHour } from '../types';
import { WEEK_DAYS } from '../utils/openingHours';

interface OpeningHoursEditorProps {
  aberto24h: boolean;
  horarios: OpeningHour[];
  onChange: (aberto24h: boolean, horarios: OpeningHour[]) => void;
}

const DEFAULT_ABRE = '06:00';
const DEFAULT_FECHA = '22:00';

const timeInputClass =
  'px-3 py-2 bg-[#0f1419] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#089453] disabled:opacity-40 [color-scheme:dark]';

/**
 * Horário de funcionamento por dia da semana (um intervalo por dia).
 * O app usa esse horário para mostrar só os postos abertos.
 */
const OpeningHoursEditor: React.FC<OpeningHoursEditorProps> = ({ aberto24h, horarios, onChange }) => {
  const doDia = (dia: number) => horarios.find((h) => h.diaSemana === dia);

  const setDia = (dia: number, horario: OpeningHour | null) => {
    const outros = horarios.filter((h) => h.diaSemana !== dia);
    onChange(false, horario ? [...outros, horario] : outros);
  };

  const copiarParaTodos = (origem: OpeningHour) => {
    onChange(
      false,
      WEEK_DAYS.map(({ dia }) => ({ ...origem, diaSemana: dia }))
    );
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 text-white cursor-pointer">
        <input
          type="checkbox"
          checked={aberto24h}
          onChange={(e) => onChange(e.target.checked, e.target.checked ? [] : horarios)}
          className="w-5 h-5 accent-[#089453]"
        />
        Aberto 24 horas, todos os dias
      </label>

      {!aberto24h && (
        <div className="rounded-xl border border-white/10 divide-y divide-white/5">
          {WEEK_DAYS.map(({ dia, nome }) => {
            const horario = doDia(dia);
            const viraNoite = !!horario && horario.fecha < horario.abre;
            return (
              <div key={dia} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <label className="flex items-center gap-2 w-32 text-white/90 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!horario}
                    onChange={(e) =>
                      setDia(dia, e.target.checked ? { diaSemana: dia, abre: DEFAULT_ABRE, fecha: DEFAULT_FECHA } : null)
                    }
                    className="w-4 h-4 accent-[#089453]"
                  />
                  {nome}
                </label>
                {horario ? (
                  <>
                    <input
                      type="time"
                      value={horario.abre}
                      onChange={(e) => setDia(dia, { ...horario, abre: e.target.value })}
                      className={timeInputClass}
                      aria-label={`${nome}: abre às`}
                      required
                    />
                    <span className="text-white/50 text-sm">às</span>
                    <input
                      type="time"
                      value={horario.fecha}
                      onChange={(e) => setDia(dia, { ...horario, fecha: e.target.value })}
                      className={timeInputClass}
                      aria-label={`${nome}: fecha às`}
                      required
                    />
                    {viraNoite && <span className="text-xs text-white/50">fecha no dia seguinte</span>}
                    <button
                      type="button"
                      onClick={() => copiarParaTodos(horario)}
                      className="ml-auto text-xs text-[#84cc34] hover:text-[#089453]"
                    >
                      Copiar para todos os dias
                    </button>
                  </>
                ) : (
                  <span className="text-white/40 text-sm">Fechado</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const hasOpeningHours = (aberto24h?: boolean, horarios?: OpeningHour[]) =>
  !!aberto24h || (horarios?.length ?? 0) > 0;

export default OpeningHoursEditor;
